import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Public as PublicIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import learningMaterialService, { LearningMaterial } from '../../services/learningMaterialService';
import LearningMaterialForm from './LearningMaterialForm';
import LearningMaterialContent from './LearningMaterialContent';
import 'quill/dist/quill.snow.css';

interface LearningMaterialListProps {
  lessonId: number;
  isEditable: boolean;
}

const LearningMaterialList: React.FC<LearningMaterialListProps> = ({
  lessonId,
  isEditable,
}) => {
  const [materials, setMaterials] = useState<LearningMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<LearningMaterial | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState<LearningMaterial | null>(null);

  const loadMaterials = async () => {
    try {
      setLoading(true);
      const data = await learningMaterialService.getMaterialsByLesson(lessonId);
      setMaterials(data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load materials:', err);
      setError('Materiaalien lataus epäonnistui');
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    loadMaterials();
  }, [lessonId]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleCreateNew = () => {
    setEditingMaterial(undefined);
    setShowForm(true);
  };

  const handleEdit = (material: LearningMaterial) => {
    setEditingMaterial(material);
    setShowForm(true);
  };

  const handleSave = () => {
    setShowForm(false);
    setEditingMaterial(undefined);
    loadMaterials();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingMaterial(undefined);
  };

  const handleDeleteClick = (material: LearningMaterial) => {
    setMaterialToDelete(material);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!materialToDelete) return;

    try {
      await learningMaterialService.deleteMaterial(materialToDelete.id);
      setDeleteDialogOpen(false);
      setMaterialToDelete(null);
      loadMaterials();
    } catch (err: any) {
      console.error('Failed to delete material:', err);
      setError('Materiaalin poisto epäonnistui');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setMaterialToDelete(null);
  };

  if (showForm) {
    return (
      <LearningMaterialForm
        lessonId={lessonId}
        material={editingMaterial}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    );
  }

  if (loading) {
    return <Typography>Ladataan materiaaleja...</Typography>;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isEditable && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            Luo materiaali
          </Button>
        </Box>
      )}

      {materials.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary" align="center">
              Ei vielä materiaaleja.
              {isEditable && ' Klikkaa "Luo materiaali" lisätäksesi.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {materials.map((material) => (
            <Card key={material.id}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6">{material.title}</Typography>
                      <Chip
                        size="small"
                        icon={material.isPublic ? <PublicIcon /> : <LockIcon />}
                        label={material.isPublic ? 'Julkinen' : 'Yksityinen'}
                        color={material.isPublic ? 'success' : 'default'}
                      />
                    </Box>
                    {material.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {material.description}
                      </Typography>
                    )}
                  </Box>
                  {isEditable && (
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(material)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(material)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                <Box
                  className="ql-editor"
                  sx={{
                    padding: 0,
                    '& img': { maxWidth: '100%', height: 'auto' },
                    '& video': { maxWidth: '100%', height: 'auto' },
                  }}
                >
                  <LearningMaterialContent material={material} />
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  Luonut {material.createdBy?.firstName} {material.createdBy?.lastName} •{' '}
                  {new Date(material.createdAt).toLocaleDateString('fi-FI')}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Poista materiaali</DialogTitle>
        <DialogContent>
          <Typography>
            Haluatko varmasti poistaa materiaalin "{materialToDelete?.title}"? Tätä toimintoa ei voi perua.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Peruuta</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Poista
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LearningMaterialList;
